import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import '../../styles/MyStatistics.css';
import {
  FiBarChart2,
  FiBookOpen,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiDatabase,
  FiSettings,
  FiTarget,
  FiUser,
} from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { HiUsers } from 'react-icons/hi';

import TitleSetter from '../TitleSetter';
import TopBar from '../TopBar';
import StatsHub from '../Statshub';
import PlayerDashboard from '../PlayerDashboard';
import DashboardOverview from '../DashboardOverview';
import LegacyDashboardOverview from '../LegacyDashboardOverview';
import HeadToHead from '../HeadToHead';
import RecentSubmissions from './components/RecentSubmissions';
import MatchRoom from './MatchRoom/MatchRoom';
import PlayerRoles from '../P.Dashboard/PlayerRoles';

import { useMyStatsTeamData } from './hooks/useMyStatsTeamData';
import { useRecentSubmissions } from './hooks/useRecentSubmissions';
import { canAccessDatabaseConfig, canDeleteRecentSubmissions, isPlayerRole } from '../../utils/teamRole';
import { getCurrentSeason } from '../../data/seasons';

const getRecentDeleteConfirmation = (item) => {
  const lines = ['Delete this submission?'];
  const isTeamSummary = item?.type === 'Team Summary' || item?.type === 'Team Match';
  const isEnemy = item?.type === 'Enemy Scouting';

  if (isTeamSummary && (item?.reciprocalModeId || item?.reciprocalSeasonId)) {
    lines.push('This will also delete the linked opponent scouting record created from this match.');
  } else if (isEnemy && item?.mirrorId) {
    lines.push('This will also delete its mirrored opponent scouting record.');
  } else if (
    isEnemy &&
    item?.source === 'teamSummaryReciprocal' &&
    item?.sourceCollection === 'teamStats' &&
    item?.sourceModeId
  ) {
    lines.push('This may also remove the linked Team Summary source record.');
  } else {
    lines.push('This action cannot be undone.');
  }

  if (!lines.includes('This action cannot be undone.')) {
    lines.push('This action cannot be undone.');
  }

  return lines.join('\n\n');
};

const SidebarNavButton = ({
  active = false,
  collapsed = false,
  icon: Icon,
  label,
  onClick,
}) => (
  <button
    type="button"
    className={`sidebar-btn ${active ? 'active' : ''}`}
    onClick={onClick}
    title={label}
    aria-current={active ? 'page' : undefined}
  >
    {Icon && <Icon className="sidebar-btn-icon" size={18} aria-hidden="true" />}
    {!collapsed && <span className="sidebar-btn-label">{label}</span>}
  </button>
);

const TeamSummaryForm = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardVersion, setDashboardVersion] = useState(() => {
    try { return localStorage.getItem('myStats:teamDashboardVersion') === 'classic' ? 'classic' : 'v2'; }
    catch { return 'v2'; }
  });
  const [playerRequest, setPlayerRequest] = useState(null);
  const [recentMenuOpen, setRecentMenuOpen] = useState(false);
  const [recentSection, setRecentSection] = useState('team');
  const [recentSeason, setRecentSeason] = useState(String(getCurrentSeason()));
  const navigate = useNavigate();

  const switchDashboardVersion = (version) => {
    setDashboardVersion(version);
    try { localStorage.setItem('myStats:teamDashboardVersion', version); }
    catch { /* The switch still works when browser storage is unavailable. */ }
  };

  const {
    teamName,
    userProfile,
    uid,
    teamId,
    teamLogo,
    setUserProfile,
    setTeamLogo,
  } = useMyStatsTeamData();
  const isPlayer = isPlayerRole(userProfile?.role);
  const canAccessCoachPanels = !isPlayer;
  const canAccessAdminDatabase = canAccessDatabaseConfig(userProfile?.role);
  const canDeleteRecent = canDeleteRecentSubmissions(userProfile?.role);

  const {
    recentLoading,
    recentSubmissions,
    availableSeasons,
    deleteRecentSubmission,
  } = useRecentSubmissions({
    activeTab,
    teamId,
    uid,
    selectedSeason: recentSeason,
  });

  useEffect(() => {
    const savedTab = sessionStorage.getItem('returnToTab');
    const validTabs = new Set([
      'dashboard',
      'playerDashboard',
      'headtohead',
    ]);
    if (canAccessCoachPanels) {
      validTabs.add('statshub');
      validTabs.add('playerAssignments');
      validTabs.add('matchRoom');
      validTabs.add('recentSubmissions');
    }
    if (savedTab && validTabs.has(savedTab)) {
      setActiveTab(savedTab);
    }
    sessionStorage.removeItem('returnToTab');
  }, [canAccessCoachPanels]);

  useEffect(() => {
    if (!canAccessCoachPanels && (activeTab === 'statshub' || activeTab === 'playerAssignments' || activeTab === 'matchRoom' || activeTab === 'recentSubmissions')) {
      setActiveTab('dashboard');
    }
  }, [activeTab, canAccessCoachPanels]);

  useEffect(() => {
    if (activeTab !== 'recentSubmissions') {
      setRecentMenuOpen(false);
    }
  }, [activeTab]);

  return (
    <>
      <TitleSetter title="MyStats" />
      <div className="container">
        <TopBar
          userProfile={userProfile}
          teamLogo={teamLogo}
          setUserProfile={setUserProfile}
          setTeamLogo={setTeamLogo}
        />

        <div className={`stat-page-layout ${activeTab === 'dashboard' ? 'team-dashboard-active' : ''}`}>
          <div className={`left-panel ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="panel-header">
              {!isCollapsed && <h2>{teamName ? `${teamName} Database` : 'THEBOX Database'}</h2>}

              <button
                type="button"
                className="collapse-btn"
                onClick={() => setIsCollapsed((prev) => !prev)}
                title={isCollapsed ? 'Expand MyStats navigation' : 'Collapse MyStats navigation'}
                aria-label={isCollapsed ? 'Expand MyStats navigation' : 'Collapse MyStats navigation'}
              >
                {isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
              </button>
            </div>

            {!isCollapsed && <div className="sidebar-section-label">Performance</div>}

            <SidebarNavButton
              active={activeTab === 'dashboard'}
              collapsed={isCollapsed}
              icon={FiBarChart2}
              label="Team Dashboard"
              onClick={() => setActiveTab('dashboard')}
            />

            <SidebarNavButton
              active={activeTab === 'playerDashboard'}
              collapsed={isCollapsed}
              icon={FiUser}
              label="Players"
              onClick={() => setActiveTab('playerDashboard')}
            />

            <SidebarNavButton
              active={activeTab === 'headtohead'}
              collapsed={isCollapsed}
              icon={HiUsers}
              label="Head-to-Head"
              onClick={() => setActiveTab('headtohead')}
            />

            {canAccessCoachPanels && !isCollapsed && <div className="sidebar-section-label">Preparation</div>}

            {canAccessCoachPanels && (
              <SidebarNavButton
                active={activeTab === 'matchRoom'}
                collapsed={isCollapsed}
                icon={FiTarget}
                label="Match Room"
                onClick={() => setActiveTab('matchRoom')}
              />
            )}

            {canAccessCoachPanels && (
              <SidebarNavButton
                active={activeTab === 'playerAssignments'}
                collapsed={isCollapsed}
                icon={FiUser}
                label="Player Assignments"
                onClick={() => setActiveTab('playerAssignments')}
              />
            )}

            {canAccessCoachPanels && (
              <SidebarNavButton
                collapsed={isCollapsed}
                icon={FiBookOpen}
                label="Playbook"
                onClick={() => navigate('/my-playbook')}
              />
            )}

            {canAccessCoachPanels && !isCollapsed && <div className="sidebar-section-label">Data</div>}

            {canAccessCoachPanels && (
              <SidebarNavButton
                active={activeTab === 'statshub'}
                collapsed={isCollapsed}
                icon={FiDatabase}
                label="Stats Hub"
                onClick={() => setActiveTab('statshub')}
              />
            )}

            {canAccessCoachPanels && (
              <div className={`sidebar-group ${recentMenuOpen && !isCollapsed ? 'open' : ''}`}>
                <button
                  type="button"
                  className={`sidebar-btn recent-submissions-btn ${activeTab === 'recentSubmissions' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab('recentSubmissions');
                    if (!isCollapsed) setRecentMenuOpen((prev) => !prev);
                  }}
                  title="Recent Submissions"
                  aria-current={activeTab === 'recentSubmissions' ? 'page' : undefined}
                  aria-expanded={!isCollapsed ? recentMenuOpen : undefined}
                >
                  <FiClock className="sidebar-btn-icon" size={18} aria-hidden="true" />
                  {!isCollapsed && <span className="sidebar-btn-label">Recent Submissions</span>}
                </button>

                {!isCollapsed && (
                  <div className={`sidebar-submenu ${recentMenuOpen ? 'open' : ''}`}>
                    <button
                      type="button"
                      className={`sidebar-subitem ${recentSection === 'team' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('recentSubmissions');
                        setRecentMenuOpen(true);
                        setRecentSection('team');
                      }}
                    >
                      Team Summary
                    </button>
                    <button
                      type="button"
                      className={`sidebar-subitem ${recentSection === 'player' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('recentSubmissions');
                        setRecentMenuOpen(true);
                        setRecentSection('player');
                      }}
                    >
                      Player Stats
                    </button>
                    <button
                      type="button"
                      className={`sidebar-subitem ${recentSection === 'enemy' ? 'active' : ''}`}
                      onClick={() => {
                        setActiveTab('recentSubmissions');
                        setRecentMenuOpen(true);
                        setRecentSection('enemy');
                      }}
                    >
                      Enemy Scouting
                    </button>
                  </div>
                )}
              </div>
            )}

            {canAccessCoachPanels && canAccessAdminDatabase && !isCollapsed && (
              <div className="sidebar-section-label">Administration</div>
            )}

            {canAccessCoachPanels && canAccessAdminDatabase && (
              <SidebarNavButton
                collapsed={isCollapsed}
                icon={FiSettings}
                label="Database Configuration"
                onClick={() => navigate('/settings?tab=database-config')}
              />
            )}
          </div>

          <div className="main-panel">
            {canAccessCoachPanels && activeTab === 'statshub' && teamId && (
              <StatsHub
                teamId={teamId}
                onViewRecentSubmissions={() => {
                  setActiveTab('recentSubmissions');
                  setRecentMenuOpen(true);
                }}
              />
            )}

            <div style={{ display: activeTab === 'playerDashboard' ? 'block' : 'none' }}>
              <PlayerDashboard requestedPlayer={playerRequest} onPlayerRequestHandled={() => setPlayerRequest(null)} />
            </div>

            <div style={{ display: activeTab === 'dashboard' ? 'block' : 'none' }}>
              <div className="team-dashboard-version-bar">
                <div className="team-dashboard-version-switch" role="group" aria-label="Team Dashboard version">
                  <button type="button" aria-pressed={dashboardVersion === 'v2'}
                    onClick={() => switchDashboardVersion('v2')}>New Dashboard</button>
                  <button type="button" aria-pressed={dashboardVersion === 'classic'}
                    onClick={() => switchDashboardVersion('classic')}>Classic Dashboard</button>
                </div>
              </div>
              {dashboardVersion === 'classic' ? <LegacyDashboardOverview /> :
                <DashboardOverview onViewPlayer={(player) => {
                  setPlayerRequest(player);
                  setActiveTab('playerDashboard');
                }} />}
            </div>

            {activeTab === 'headtohead' && <HeadToHead />}

            {canAccessCoachPanels && activeTab === 'playerAssignments' && (
              <PlayerRoles
                teamId={teamId}
                uid={uid}
                selectedSeason={recentSeason}
              />
            )}

            {canAccessCoachPanels && activeTab === 'matchRoom' && (
              <MatchRoom teamName={teamName} teamId={teamId} uid={uid} />
            )}

            {canAccessCoachPanels && activeTab === 'recentSubmissions' && (
              <RecentSubmissions
                loading={recentLoading}
                sections={recentSubmissions}
                sectionFilter={recentSection}
                seasonOptions={availableSeasons}
                selectedSeason={recentSeason}
                onSeasonChange={setRecentSeason}
                onEdit={(path) => {
                  sessionStorage.setItem('returnToTab', 'recentSubmissions');
                  navigate(path);
                }}
                canDelete={canDeleteRecent}
                onDelete={async (item) => {
                  if (!canDeleteRecent) return;
                  const confirmed = window.confirm(getRecentDeleteConfirmation(item));
                  if (!confirmed) return;

                  const result = await deleteRecentSubmission(item);
                  if (result.ok) {
                    toast.success('Submission deleted.');
                    return;
                  }

                  toast.error('Failed to delete submission.');
                }}
              />
            )}
          </div>
        </div>

        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </>
  );
};

export default TeamSummaryForm;
